import { useState, useEffect, useRef, useCallback } from "react";
import { planilhaService, LockInfo } from "../services/planilhaService";

interface UsePlanilhaLockReturn {
  isOwner: boolean;
  isReadOnly: boolean;
  lockedBy: string | undefined;
  expiraEm: Date | undefined;
  lockLoading: boolean;
}

const HEARTBEAT_INTERVAL_MS = 30_000; // 30 segundos
const INACTIVITY_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutos
const LS_KEY = (id: string) => `planilha_editing_${id}`;

/** ID único por aba do navegador — persiste durante reloads na mesma aba */
function getTabId(): string {
  let id = sessionStorage.getItem("_tab_id");
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem("_tab_id", id);
  }
  return id;
}

const TAB_ID = getTabId();

/**
 * `ativo: false` não pega o lock: é o caso do perfil Visualizador, que só
 * consulta. Pegar o lock ao abrir travaria a planilha para um Admin que
 * precisasse editá-la enquanto alguém só olha.
 */
export function usePlanilhaLock(
  planilhaId: string,
  { ativo = true }: { ativo?: boolean } = {},
): UsePlanilhaLockReturn {
  const [lockInfo, setLockInfo] = useState<LockInfo | null>(null);
  const [lockLoading, setLockLoading] = useState(true);

  // Rastear atividade do usuário
  const hasActivityRef = useRef(false);
  const lastActivityRef = useRef(Date.now());
  const isOwnerRef = useRef(false);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleActivity = useCallback(() => {
    hasActivityRef.current = true;
    lastActivityRef.current = Date.now();
  }, []);

  // Liberar lock ao fechar/navegar (sendBeacon para garantia)
  const releaseOnUnload = useCallback(() => {
    if (isOwnerRef.current) {
      // Remover entrada do localStorage imediatamente
      try {
        const raw = localStorage.getItem(LS_KEY(planilhaId));
        if (raw) {
          const entry: { tabId: string } = JSON.parse(raw);
          if (entry.tabId === TAB_ID)
            localStorage.removeItem(LS_KEY(planilhaId));
        }
      } catch {
        /* noop */
      }

      // Para liberar imediatamente no backend: fetch keepalive
      const token = localStorage.getItem("auth_token");
      const baseUrl =
        import.meta.env.VITE_API_BASE_URL ||
        "https://monu-cotacoes-api-fkgbfraqd4hqc7da.brazilsouth-01.azurewebsites.net/api";
      fetch(`${baseUrl}/planilhas/${planilhaId}/lock`, {
        method: "DELETE",
        keepalive: true,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    }
  }, [planilhaId]);

  useEffect(() => {
    let cancelled = false;

    if (!ativo) {
      setLockInfo(null);
      setLockLoading(false);
      return;
    }

    /** Verifica se outra aba do mesmo navegador já possui o lock */
    function checkLocalTabOwner(): boolean {
      try {
        const raw = localStorage.getItem(LS_KEY(planilhaId));
        if (!raw) return false;
        const entry: { tabId: string; ts: number } = JSON.parse(raw);
        // Considerar válido por até 5 minutos (cobre TTL do backend + margem)
        const VALID_MS = 5 * 60 * 1000;
        return entry.tabId !== TAB_ID && Date.now() - entry.ts < VALID_MS;
      } catch {
        return false;
      }
    }

    function claimLocalOwnership() {
      localStorage.setItem(
        LS_KEY(planilhaId),
        JSON.stringify({ tabId: TAB_ID, ts: Date.now() }),
      );
    }

    function releaseLocalOwnership() {
      const raw = localStorage.getItem(LS_KEY(planilhaId));
      if (!raw) return;
      try {
        const entry: { tabId: string } = JSON.parse(raw);
        if (entry.tabId === TAB_ID) {
          localStorage.removeItem(LS_KEY(planilhaId));
        }
      } catch {
        /* noop */
      }
    }

    async function acquireLock() {
      setLockLoading(true);
      try {
        // Se outra aba do mesmo navegador já está editando → leitura imediata
        if (checkLocalTabOwner()) {
          if (!cancelled) {
            setLockInfo({ bloqueada: true, bloqueadaPor: "você em outra aba" });
            isOwnerRef.current = false;
          }
          return;
        }

        const result = await planilhaService.acquireLock(planilhaId);
        if (cancelled) return;
        setLockInfo(result);
        const owned = !result.bloqueada;
        isOwnerRef.current = owned;
        if (owned) claimLocalOwnership();
      } catch {
        if (!cancelled) setLockInfo(null);
      } finally {
        if (!cancelled) setLockLoading(false);
      }
    }

    acquireLock();

    // Detectar quando outra aba reivindica o lock via localStorage
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== LS_KEY(planilhaId)) return;
      if (!e.newValue) return; // outra aba liberou — não muda nada aqui
      try {
        const entry: { tabId: string } = JSON.parse(e.newValue);
        if (entry.tabId !== TAB_ID && isOwnerRef.current) {
          // Outra aba reivindicou o lock — esta aba cede
          isOwnerRef.current = false;
          setLockInfo({ bloqueada: true, bloqueadaPor: "você em outra aba" });
        }
      } catch {
        /* noop */
      }
    };
    window.addEventListener("storage", handleStorageChange);

    // Registrar activity listeners
    const events = ["mousemove", "keydown", "scroll", "click", "touchstart"];
    events.forEach((e) => window.addEventListener(e, handleActivity));
    window.addEventListener("beforeunload", releaseOnUnload);

    // Heartbeat: a cada 30s verifica inatividade; se ativo, renova
    heartbeatTimerRef.current = setInterval(async () => {
      if (!isOwnerRef.current) return;

      const idleSince = Date.now() - lastActivityRef.current;
      if (idleSince >= INACTIVITY_TIMEOUT_MS) {
        // Inatividade >= 2min — liberar lock e sair do modo owner
        await planilhaService.releaseLock(planilhaId);
        isOwnerRef.current = false;
        releaseLocalOwnership();
        setLockInfo((prev) => ({ ...prev, bloqueada: false }) as LockInfo);
        return;
      }

      if (hasActivityRef.current) {
        hasActivityRef.current = false;
        await planilhaService.heartbeatLock(planilhaId);
        // Renovar timestamp local
        claimLocalOwnership();
      }
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      cancelled = true;
      events.forEach((e) => window.removeEventListener(e, handleActivity));
      window.removeEventListener("beforeunload", releaseOnUnload);
      window.removeEventListener("storage", handleStorageChange);

      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
      }

      // Liberar lock ao desmontar o componente (ex: navegar para outra página)
      if (isOwnerRef.current) {
        planilhaService.releaseLock(planilhaId);
        releaseLocalOwnership();
        isOwnerRef.current = false;
      }
    };
  }, [planilhaId, handleActivity, releaseOnUnload, ativo]);

  const isOwner = !lockInfo?.bloqueada;
  const isReadOnly = lockInfo !== null && lockInfo.bloqueada === true;

  return {
    isOwner,
    isReadOnly,
    lockedBy: lockInfo?.bloqueadaPor,
    expiraEm: lockInfo?.expiraEm ? new Date(lockInfo.expiraEm) : undefined,
    lockLoading,
  };
}
